import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
} from "@mui/material";
import {
  CalendarToday,
  Timeline,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";

export interface EmptyNutritionHistoryData {
  data: [];
  resumo: string;
  url: string;
}

export interface EmptyNutritionHistoryViewProps {
  emptyNutritionData: EmptyNutritionHistoryData;
}

export const EmptyNutritionHistoryView: React.FC<
  EmptyNutritionHistoryViewProps
> = ({ emptyNutritionData }) => {
  const { t } = useTranslation(['animal', 'common']);
  const isAnimal = emptyNutritionData.url === "/nutricao/busca_animal";
  const entityType = isAnimal ? t('common:entities.animal') : t('common:entities.lot');

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
        minHeight: 0,
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#c1c1c1",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#a8a8a8",
        },
      }}
    >
      <Box sx={{ p: 4 }}>
        {/* Header com informações */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              {t('animal:nutrition_history.title')}
            </Typography>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <Chip
                label={t('animal:nutrition_history.type_label', { type: entityType })}
                size="small"
                sx={{
                  bgcolor: DESIGN_TOKENS.colors.neutral[100],
                  color: DESIGN_TOKENS.colors.text.primary,
                  fontFamily: "monospace",
                  fontWeight: 600,
                }}
              />
              <Chip
                label={t('animal:nutrition_history.records_count', { count: 0 })}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>

            {emptyNutritionData.resumo && (
              <Typography
                variant="body2"
                sx={{
                  color: DESIGN_TOKENS.colors.text.secondary,
                  fontStyle: "italic",
                }}
              >
                {emptyNutritionData.resumo}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Estatísticas */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {t('animal:nutrition_history.statistics_title', { entity: entityType })}
            </Typography>
            <Stack direction="row" spacing={4} sx={{ flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img 
                  src="/vaca.png" 
                  alt={t('common:alt_texts.animal')} 
                  style={{ width: 24, height: 24, filter: 'brightness(0)' }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {isAnimal ? t('common:entities.animal') : t('animal:nutrition_history.total_animals')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {isAnimal ? t('animal:nutrition_history.not_identified') : "0"}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Timeline
                  sx={{ color: DESIGN_TOKENS.colors.organization.farm }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('animal:nutrition_history.feeding_records')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    0
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarToday
                  sx={{ color: DESIGN_TOKENS.colors.organization.farm }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('animal:nutrition_history.last_record')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('animal:nutrition_history.none')}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img 
                  src="/forragem.png" 
                  alt={t('common:alt_texts.forage')} 
                  style={{ width: 24, height: 24, filter: 'brightness(0)' }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('animal:nutrition_history.forage')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('animal:nutrition_history.none')}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Estado vazio */}
        <Card>
          <CardContent sx={{ textAlign: "center", py: 8 }}>
            <img 
              src="/forragem.png" 
              alt={t('common:alt_texts.forage')} 
              style={{ 
                width: 64, 
                height: 64, 
                marginBottom: 16, 
                opacity: 0.3,
                filter: 'grayscale(100%)'
              }}
            />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {t('animal:nutrition_history.no_history_found')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {emptyNutritionData.resumo}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isAnimal 
                ? t('animal:nutrition_history.no_animal_records') 
                : t('animal:nutrition_history.no_lot_records')}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default EmptyNutritionHistoryView;